import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { Button, Paper, Menu, MenuItem } from "@material-ui/core";
import ResponsiveTable from "../table/ResponsiveTable";
import TableRowProfile from "../util/TableRowProfile";
import ConfirmationModal from "../modal/ConfirmationModal";
import Loading from "../loadingSpinner/Loading";
import { ProfileRelationType } from "../../types/enum";
import blankProfile from "../../img/hotgig-loading.png";

@inject("agentActStore", "templateStore", "userInvitationStore", "profileStore")
@observer
class InvitedActsTable extends Component {
    state = {
        actionAnchor: [],
        deleteInvitationModalState: false,
        resentLinkConfirmationModalState: false,
        deletedAct: undefined,
        resendAct: undefined
    };

    componentDidMount() {
        this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.AgentAct.ordinal);
    }

    columns = [
        { key: "actName", label: "Act Name", width: "25%", sortable: true, isString: true, addComp: true },
        { key: "actOwner", label: "Act Owner", width: "25%", sortable: true, isString: true, addComp: true },
        { key: "status", label: "Status", width: "15%" },
        { key: "action", label: "", width: "35%" }
    ];

    setActionAnchor = (element, id) => {
        var tempAnchor = [];
        tempAnchor[id] = element;
        this.setState({ ...this.state, actionAnchor: tempAnchor });
    };

    unsetActionAnchor = id => {
        this.setState({ ...this.state, actionAnchor: [] });
    };

    handleOpenDeleteInvitationModal = invitation => {
        this.setState({
            ...this.state,
            deleteInvitationModalState: true,
            deletedAct: invitation
        });
    };

    handleCloseDeleteModal = () => {
        this.setState({
            ...this.state,
            deleteInvitationModalState: false,
            deletedAct: undefined,
            resentLinkConfirmationModalState: false,
            resendAct: undefined
        });
    };

    handleDeleteInvitation = () => {
        this.props.userInvitationStore.deleteInvitation(this.state.deletedAct).then(() => {
            this.props.templateStore.openSnackbar(this.state.deletedAct.invitedAct + " has been removed.");
            this.handleCloseDeleteModal();
        });
    };

    handleOpenResendInvitationModal = invitation => {
        this.setState({
            ...this.state,
            resentLinkConfirmationModalState: true,
            resendAct: invitation
        });
    };

    handleResendInvitation = () => {
        this.props.userInvitationStore.resendInvitation(this.state.resendAct).then(() => {
            this.props.templateStore.openSnackbar("The invitation to " + this.state.resendAct.invitedAct + " has been resent");
            this.handleCloseDeleteModal();
        }).catch(() => {
            this.props.templateStore.openSnackbar("Failed to resend invitation to this account, please contact admin");
        }).finally(() => {
            this.handleCloseDeleteModal();
        });
    };

    mapInvitations = () => {
        const rows = [];
        const { invitations } = this.props.userInvitationStore;
        
        invitations.filter(data => !data.additionalData).forEach(data => {
            var row = {};

            row.actOwner = {
                newFormat: (
                    <TableRowProfile 
                        name={`${data.invitedFirstName} ${data.invitedLastName}`} 
                        avatarUrl={data.ownerImage ? data.ownerImage : blankProfile} 
                        profileId={data.invitedVenue}
                    />
                ),
                value: `${data.invitedFirstName} ${data.invitedLastName}`
            };

            row.actName = {
                newFormat: (
                    <TableRowProfile 
                        name={data.invitedAct} 
                        avatarUrl={data.actImage ? data.actImage : blankProfile} 
                    />
                ),
                value: data.invitedAct
            };

            row.status = (
                <span style={{
                    display: "inline-block",
                    padding: "4px 12px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    background: "#e3f2fd",
                    color: "#1565c0"
                }}>
                    Invited
                </span>
            );

            row.action = (
                <Fragment>
                    <Button color="primary" size="small" onClick={e => this.setActionAnchor(e.currentTarget, data.id)}>
                        <i className="fa-solid fa-ellipsis-vertical fa-xl"></i>
                    </Button>
                    {data.id && (
                        <Menu
                            open={this.state.actionAnchor[data.id] != null}
                            onClose={() => this.unsetActionAnchor(data.id)}
                            anchorEl={this.state.actionAnchor[data.id]}
                        >
                            <MenuItem onClick={() => this.handleOpenResendInvitationModal(data)}>Resend Invitation</MenuItem>
                            <MenuItem onClick={() => this.handleOpenDeleteInvitationModal(data)}>Cancel Invitation</MenuItem>
                        </Menu>
                    )}
                </Fragment>
            );

            row.style = {
                backgroundColor: "#f5f5f5"
            };

            rows.push(row);
        });

        return rows;
    };

    render() {
        const { isLoadingUserInvitations } = this.props.userInvitationStore;
        const rows = this.mapInvitations();

        return (
            <Fragment>
                <Paper style={{ padding: 30, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                    <p style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>
                        Acts you've invited to join your roster
                    </p>
                    
                    <div style={{ position: "relative" }}>
                        <Loading showed={isLoadingUserInvitations} />
                        {rows.length > 0 ? (
                            <ResponsiveTable
                                columns={this.columns}
                                rows={rows}
                                showToolbar={true}
                                title="Invited Acts"
                                pagination={rows.length > 5}
                                sortable={true}
                            />
                        ) : (
                            <div style={{ textAlign: "center", padding: "60px 40px", color: "#999" }}>
                                <i className="fas fa-envelope" style={{ fontSize: 48, marginBottom: 16, color: "#ddd" }}></i>
                                <h3 style={{ color: "#666", marginBottom: 8 }}>No Pending Invitations</h3>
                                <p>You haven't invited any acts yet. Use the "Add Act" button to invite acts to your roster.</p>
                            </div>
                        )}
                    </div>
                </Paper>

                <ConfirmationModal
                    open={this.state.deleteInvitationModalState}
                    message="Are you sure you want to cancel this invitation?"
                    closeHandler={this.handleCloseDeleteModal}
                    confirmationHandler={this.handleDeleteInvitation}
                    declineHandler={this.handleCloseDeleteModal}
                    confirmationLabel="Cancel Invitation"
                />

                <ConfirmationModal
                    open={this.state.resentLinkConfirmationModalState}
                    message="Are you sure you want to resend this invitation?"
                    closeHandler={this.handleCloseDeleteModal}
                    confirmationHandler={this.handleResendInvitation}
                    declineHandler={this.handleCloseDeleteModal}
                    confirmationLabel="Resend"
                />
            </Fragment>
        );
    }
}

export default InvitedActsTable;
